import { Search } from '@/shared/components/Search';
import { AccountActionButton } from '@/shared/components';
import { Button } from '@/shared/components/ui/button';
import ArrowDownIcon from '@/shared/assets/arrow-down-icon.svg?react';
import ArrowTopRightIcon from '@/shared/assets/arrow-top-right-icon.svg?react';
import TransferIcon from '@/shared/assets/arrows-down-up-icon.svg?react';
import SidebarCloseIcon from '@/shared/assets/header/sidebar-close.svg?react';
import SidebarOpenIcon from '@/shared/assets/header/sidebar-open.svg?react';
import SettingsIcon from '@/shared/assets/header/settings.svg?react';
import { useMediaQuery } from '@/shared/lib';
import { useTranslation } from 'react-i18next';
import { HEADER_SEARCH_ENABLED } from '@/shared/config';
import { AuthorizedLayoutMenuButton } from './AuthorizedLayoutMenuButton';
import { useGlobalSearchStore } from '@/widgets/global-search';
import { useSidebarStore } from './sidebar';
import { UserBalanceDropdown } from './user-balance-dropdown';
import SearchIcon from '@/shared/assets/header/search.svg?react';

type Props = {
  onDepositClick: () => void;
  onWithdrawClick: () => void;
  onTransferClick: () => void;
  onSettingsClick: () => void;
  isScrolled?: boolean;
  buttonsVisibility: {
    deposit: boolean;
    withdraw: boolean;
    transfer: boolean;
  };
};

function AuthorizedLayoutHeader({
  onDepositClick,
  onWithdrawClick,
  onTransferClick,
  onSettingsClick,
  isScrolled = false,
  buttonsVisibility,
}: Props) {
  const { t } = useTranslation();
  const isTabletUp = useMediaQuery('(min-width: 1068px)');
  const { isCollapsed, toggleCollapsed, isMobileOpen, toggleMobile } =
    useSidebarStore();
  const { open: openGlobalSearch } = useGlobalSearchStore();

  const { deposit, withdraw, transfer } = buttonsVisibility;

  return (
    <>
      <div className="relative w-full h-[5.6rem] shrink-0 z-10 flex justify-between items-center">
        <div className="absolute inset-0 bg-background/90 backdrop-blur-[8px]" />

        {isScrolled && (
          <div className="absolute inset-x-0 bottom-0 h-[0.1rem] bg-stroke-bg" />
        )}

        <header className="relative flex-1 h-[5.6rem] flex flex-row items-center gap-[0.8rem] tablet:gap-[1.2rem] py-[1.2rem] px-[1.6rem] tablet:px-[2.4rem]">
          {isTabletUp && (
            <Button
              variant="outline"
              size="icon"
              aria-label={t(
                isCollapsed
                  ? 'personalAccount.sidebarNavigation.expand'
                  : 'personalAccount.sidebarNavigation.collapse'
              )}
              onClick={toggleCollapsed}
              className="border border-stroke-bg rounded-[1rem] h-[3.2rem] w-[3.2rem] shadow-none shrink-0">
              {isCollapsed ? (
                <SidebarOpenIcon className="size-[1.6rem] text-secondary" />
              ) : (
                <SidebarCloseIcon className="size-[1.6rem] text-secondary" />
              )}
            </Button>
          )}

          <div className="order-first tablet:order-last min-w-0">
            <UserBalanceDropdown />
          </div>

          <div className="flex-1 min-w-0">
            {HEADER_SEARCH_ENABLED && isTabletUp && (
              <Search
                size="sm"
                label={t('personalAccount.header.searchPlaceholder')}
                placeholder={t('personalAccount.header.searchPlaceholder')}
                value=""
                onChange={() => {}}
                onClick={openGlobalSearch}
                onFocus={event => {
                  event.currentTarget.blur();
                  openGlobalSearch();
                }}
              />
            )}
          </div>

          {isTabletUp && (
            <div className="flex items-center gap-[1.2rem]">
              {deposit && (
                <AccountActionButton
                  icon={ArrowDownIcon}
                  size="sm"
                  allowBorder
                  className="border-stroke-bg"
                  label={t('header.actions.deposit')}
                  onClick={() => onDepositClick()}
                />
              )}

              {withdraw && (
                <AccountActionButton
                  icon={ArrowTopRightIcon}
                  size="sm"
                  allowBorder
                  className="border-stroke-bg"
                  label={t('header.actions.withdraw')}
                  onClick={() => onWithdrawClick()}
                />
              )}

              {transfer && (
                <AccountActionButton
                  icon={TransferIcon}
                  size="sm"
                  allowBorder
                  className="border-stroke-bg"
                  label={t('header.actions.transfer')}
                  onClick={() => onTransferClick()}
                />
              )}

              <AccountActionButton
                icon={SettingsIcon}
                size="sm"
                allowBorder
                label={''}
                onClick={() => onSettingsClick()}
                className="dark:bg-background dark:text-primary border-stroke-bg min-w-[3.2rem] max-w-[3.2rem] p-0"
              />
            </div>
          )}

          {HEADER_SEARCH_ENABLED && !isTabletUp && (
            <Button
              variant="outline"
              size="icon"
              aria-label={t('personalAccount.header.searchPlaceholder')}
              onClick={openGlobalSearch}
              className="border border-stroke-bg rounded-[1rem] h-[3.2rem] w-[3.2rem] shadow-none shrink-0">
              <SearchIcon className="size-[1.6rem] text-primary" />
            </Button>
          )}

          <AuthorizedLayoutMenuButton
            isMenuOpen={isMobileOpen}
            onClick={toggleMobile}
            className="tablet:hidden z-[60] shrink-0"
          />
        </header>
      </div>

      {isMobileOpen && (
        <div className="fixed top-[1.2rem] right-[1.6rem] z-[60] tablet:hidden">
          <AuthorizedLayoutMenuButton
            isMenuOpen={isMobileOpen}
            onClick={toggleMobile}
          />
        </div>
      )}
    </>
  );
}

export { AuthorizedLayoutHeader };
